// engine/render.ts — canvas sized to the viewport, letterboxed to a 1280x720
// logical resolution, soft-follow camera, and the Draw surface handed to content.

import type { Draw, Vec } from '../types'
import { drawSprite, spriteSize, type SpriteOpts } from './sprites'
import { fx } from './fx'

export const LOGICAL_W = 1280
export const LOGICAL_H = 720

/**
 * World magnification inside the logical viewport. The HUD stays at 1:1 (crisp
 * text) while the world is drawn larger so 20-30px pixel-art bodies actually
 * read at a glance. One constant; balance/feel may retune it.
 */
export const WORLD_ZOOM = 1.5

const scratch: Vec = { x: 0, y: 0 }

export class Renderer {
  readonly canvas: HTMLCanvasElement
  readonly ctx: CanvasRenderingContext2D

  /** World position of the top-left of the logical viewport. */
  readonly camera: Vec = { x: 0, y: 0 }
  worldW = LOGICAL_W
  worldH = LOGICAL_H

  zoom = WORLD_ZOOM
  /**
   * The camera is allowed to overscan past the room's edges by this much world
   * space, so a Warden pinned against the top wall never ends up underneath the
   * HUD panels. The overscan shows the zone background, not black.
   */
  padTop = 52
  padBottom = 34
  scale = 1
  offX = 0
  offY = 0
  private cssW = 0
  private cssH = 0
  private dpr = 1
  private inWorld = false

  readonly draw: Draw

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('canvas 2d context unavailable')
    this.ctx = ctx
    this.resize()
    window.addEventListener('resize', () => this.resize())

    const self = this
    this.draw = {
      ctx,
      camera: this.camera,
      worldToScreen(p: Vec): Vec {
        scratch.x = (p.x - self.camera.x) * self.zoom + fx.offX
        scratch.y = (p.y - self.camera.y) * self.zoom + fx.offY
        return scratch
      },
      sprite(recipeId: string, p: Vec, opts?: SpriteOpts): void {
        drawSprite(ctx, recipeId, p.x, p.y, opts)
      },
      circle(p: Vec, r: number, color: string, alpha = 1): void {
        if (alpha !== 1) ctx.globalAlpha = alpha
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
        ctx.fill()
        if (alpha !== 1) ctx.globalAlpha = 1
      },
      rect(p: Vec, w: number, h: number, color: string, alpha = 1): void {
        if (alpha !== 1) ctx.globalAlpha = alpha
        ctx.fillStyle = color
        ctx.fillRect(p.x, p.y, w, h)
        if (alpha !== 1) ctx.globalAlpha = 1
      },
      text(s: string, p: Vec, size: number, color: string, align: CanvasTextAlign = 'center'): void {
        ctx.font = `700 ${size}px ui-monospace, "SF Mono", Menlo, monospace`
        ctx.textAlign = align
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#00000099'
        ctx.fillText(s, p.x + 1, p.y + 1)
        ctx.fillStyle = color
        ctx.fillText(s, p.x, p.y)
      },
    }
  }

  resize(): void {
    const cssW = Math.max(320, window.innerWidth || LOGICAL_W)
    const cssH = Math.max(200, window.innerHeight || LOGICAL_H)
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    if (cssW === this.cssW && cssH === this.cssH && dpr === this.dpr) return
    this.cssW = cssW
    this.cssH = cssH
    this.dpr = dpr
    this.canvas.width = Math.round(cssW * dpr)
    this.canvas.height = Math.round(cssH * dpr)
    this.canvas.style.width = cssW + 'px'
    this.canvas.style.height = cssH + 'px'
    const s = Math.min(this.canvas.width / LOGICAL_W, this.canvas.height / LOGICAL_H)
    this.scale = s
    this.offX = Math.floor((this.canvas.width - LOGICAL_W * s) / 2)
    this.offY = Math.floor((this.canvas.height - LOGICAL_H * s) / 2)
  }

  setWorldSize(w: number, h: number): void {
    this.worldW = w
    this.worldH = h
  }

  /** Size of the visible world rectangle, in world pixels. */
  get viewW(): number {
    return LOGICAL_W / this.zoom
  }
  get viewH(): number {
    return LOGICAL_H / this.zoom
  }

  snapCameraTo(x: number, y: number): void {
    this.camera.x = this.clampCamX(x - this.viewW / 2)
    this.camera.y = this.clampCamY(y - this.viewH / 2)
  }

  private clampCamX(x: number): number {
    const max = this.worldW - this.viewW
    if (max <= 0) return Math.round(max / 2)
    return Math.max(0, Math.min(max, x))
  }

  private clampCamY(y: number): number {
    const max = this.worldH - this.viewH
    if (max <= 0) return Math.round(max / 2)
    return Math.max(-this.padTop, Math.min(max + this.padBottom, y))
  }

  /** Exponential soft-follow: frame-rate independent, no overshoot. */
  followCamera(tx: number, ty: number, dt: number, stiffness = 6): void {
    const gx = this.clampCamX(tx - this.viewW / 2)
    const gy = this.clampCamY(ty - this.viewH / 2)
    const k = 1 - Math.exp(-stiffness * dt)
    this.camera.x += (gx - this.camera.x) * k
    this.camera.y += (gy - this.camera.y) * k
  }

  /** Clear the whole backing store, then enter logical-pixel space. */
  beginFrame(bg: string): void {
    this.resize()
    const ctx = this.ctx
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.imageSmoothingEnabled = false
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    ctx.setTransform(this.scale, 0, 0, this.scale, this.offX, this.offY)
    ctx.imageSmoothingEnabled = false
    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, LOGICAL_W, LOGICAL_H)
    ctx.clip()
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, LOGICAL_W, LOGICAL_H)
  }

  endFrame(): void {
    this.ctx.restore()
  }

  beginWorld(): void {
    const z = this.zoom
    // Snap the world origin to a whole logical pixel so the pixel grid stays put.
    const tx = Math.round((-this.camera.x) * z + fx.offX) / z
    const ty = Math.round((-this.camera.y) * z + fx.offY) / z
    this.ctx.save()
    this.ctx.scale(z, z)
    this.ctx.translate(tx, ty)
    this.inWorld = true
  }

  endWorld(): void {
    this.ctx.restore()
    this.inWorld = false
  }

  worldToScreen(x: number, y: number, out: Vec): Vec {
    out.x = (x - this.camera.x) * this.zoom + fx.offX
    out.y = (y - this.camera.y) * this.zoom + fx.offY
    return out
  }

  /** Client (mouse) coordinates -> world coordinates. */
  clientToWorld(clientX: number, clientY: number, out: Vec): Vec {
    const r = this.canvas.getBoundingClientRect()
    const devX = ((clientX - r.left) / Math.max(1, r.width)) * this.canvas.width
    const devY = ((clientY - r.top) / Math.max(1, r.height)) * this.canvas.height
    out.x = (devX - this.offX) / this.scale / this.zoom + this.camera.x
    out.y = (devY - this.offY) / this.scale / this.zoom + this.camera.y
    return out
  }

  isInWorld(): boolean {
    return this.inWorld
  }

  spriteSizeOf(id: string): number {
    return spriteSize(id)
  }
}
